import { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tag } from '@digdir/designsystemet-react';
import {
  BarChartIcon,
  FileTextIcon,
  ShieldIcon,
  TasklistIcon,
} from '@navikt/aksel-icons';
import type {
  CenterItem,
  ItemIcon,
  ItemRole,
} from '../../pages/NotificationCenter/notificationCenterView';
import {
  deadlineText,
  relativeTime,
} from '../../pages/NotificationCenter/notificationCenterView';
import { useMarkNotificationsReadMutation } from '../../hooks/queries/safetyRep/useSafetyRepWork';
import classes from './NotificationCenter.module.css';

const ICONS: Record<ItemIcon, ReactElement> = {
  document: <FileTextIcon aria-hidden />,
  round: <ShieldIcon aria-hidden />,
  survey: <BarChartIcon aria-hidden />,
  measure: <TasklistIcon aria-hidden />,
};

const ROLE_COLORS: Record<
  ItemRole,
  'neutral' | 'warning' | 'info' | 'success' | 'danger'
> = {
  teamLeader: 'neutral',
  safetyRep: 'warning',
  generalManager: 'info',
  hr: 'success',
  responsible: 'danger',
};

/** Opening a notification marks it read, as on the safety representative's page. A task leaves when it is done. */
const useOpen = (onOpen?: () => void) => {
  const markRead = useMarkNotificationsReadMutation();
  return (value: CenterItem) => {
    if (value.notification && value.notification.readAt === null)
      markRead.mutate(value.notification.id);
    onOpen?.();
  };
};

type ListProps = {
  items: CenterItem[];
  onOpen?: () => void;
  headingLevel?: 3 | 4;
};

/** What waits: a card each, with what it is about, the roles it comes from and its deadline */
export const TaskCards = ({
  items,
  onOpen,
  headingLevel = 3,
}: ListProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const open = useOpen(onOpen);
  const Title = headingLevel === 3 ? 'h3' : 'h4';
  return (
    <ul className={classes.cards}>
      {items.map((value) => {
        const deadline = deadlineText(value, t, i18n.language);
        return (
          <li key={value.key} className={classes.card}>
            <span className={classes.cardIcon}>{ICONS[value.icon]}</span>
            <div className={classes.cardBody}>
              <Title className={classes.cardTitle}>
                <RouterLink
                  to={value.link}
                  className={classes.cover}
                  onClick={() => open(value)}
                >
                  {value.title}
                </RouterLink>
              </Title>
              <div className={classes.cardMeta}>
                {value.roles.map((role) => (
                  <Tag key={role} data-size="sm" data-color={ROLE_COLORS[role]}>
                    {t(`notificationCenter.roles.${role}`)}
                  </Tag>
                ))}
                {value.meta && <span>{value.meta}</span>}
              </div>
              {deadline && (
                <span className={value.overdue ? classes.overdue : classes.due}>
                  {deadline}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

/** What only tells what happened: a line each, with a dot until it is read */
export const NewsList = ({
  items,
  onOpen,
  headingLevel = 3,
}: ListProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const open = useOpen(onOpen);
  const Title = headingLevel === 3 ? 'h3' : 'h4';
  return (
    <ul className={classes.news}>
      {items.map((value) => {
        const unread = value.group !== 'read';
        return (
          <li
            key={value.key}
            className={classes.newsItem}
            data-unread={unread || undefined}
          >
            <span className={classes.dot} aria-hidden />
            <div>
              <Title className={classes.newsTitle}>
                <RouterLink
                  to={value.link}
                  className={classes.cover}
                  onClick={() => open(value)}
                >
                  {unread && (
                    <span className="ds-sr-only">
                      {t('notificationCenter.unread')}{' '}
                    </span>
                  )}
                  {value.title}
                </RouterLink>
              </Title>
              <span className={classes.newsMeta}>
                {[
                  value.roles
                    .map((role) => t(`notificationCenter.roles.${role}`))
                    .join(', '),
                  value.date && relativeTime(value.date, t, i18n.language),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
};
